import React, { useContext, useState } from "react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

import { useFormik } from "formik";
import * as Yup from "yup";
import TextField from "@material-ui/core/TextField";
import Container from "@material-ui/core/Container";
import CssBaseline from "@material-ui/core/CssBaseline";
import Button from "@material-ui/core/Button";

import LogoPNG from "./LogoPNG";

import { AuthContext } from "../../../context/AuthContext";

import ButtonTextLoading from "../Shared/ButtonTextLoading";

import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import PersonIcon from "@mui/icons-material/Person";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import styles from "../../../styles.module.css"; // Import the CSS module
import { viewportContext } from "../../../context/ViewportContext";
import { IconButton, InputAdornment } from "@material-ui/core";
import clsx from "clsx";

function Login() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { screenType } = useContext(viewportContext);

  const { login, isAuthenticated } = useContext(AuthContext);

  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },

    onSubmit: async function (values, actions) {
      setIsSubmitting(true);
      const response = await login(values.username, values.password);

      if (isAuthenticated) {
        enqueueSnackbar("Login Success", {
          variant: "success",
        });
        navigate("/HandHygiene");
      } else if (response?.code == "ERR_BAD_REQUEST") {
        enqueueSnackbar(response.response.data.message, {
          variant: "error",
        });
      } else if (response?.code == "ERR_NETWORK") {
        enqueueSnackbar("Network error.", {
          variant: "error",
        });
      }
      setIsSubmitting(false);
    },
    // validation schema for formik using yup
    validationSchema: Yup.object({
      // username must be 6-20 and is required
      username: Yup.string().required("Username is required"),
      password: Yup.string().required("Password is required"),
    }),
  });

  return (
    <>
      <Container component="main" style={{ margin: "0 auto" }}>
        <CssBaseline />
        {!screenType.isTab ? (
          <div className={styles.loginContainer}>
            <div className={styles.leftColumn}>
              <div className={styles.logoContainer}>
                <LogoPNG
                  style={{
                    width: screenType.isTv ? "650" : "250px",
                    height: screenType.isTv ? "250px" : "50px",
                    verticalAlign: "middle",
                  }}
                />
              </div>
            </div>
            <div className={styles.rightColumn}>
              <div className={styles.containerOutline}>
                <div className={styles.container}>
                  <form
                    onSubmit={formik.handleSubmit}
                    className="form"
                    noValidate
                  >
                    <div className={styles.loginFieldsDiv}>
                      <div
                        className={`${styles.inputGrid} ${styles.userFormFields}`}
                      >
                        <TextField
                          error={
                            formik.touched.username && formik.errors.username
                          }
                          required={false}
                          fullWidth
                          id="username"
                          label="User ID"
                          name="username"
                          autoComplete="username"
                          type="username"
                          variant="outlined"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.username}
                          InputLabelProps={{
                            className: clsx(styles.userInputFields),
                          }}
                          InputProps={{
                            endAdornment: (
                              <PersonIcon
                                style={{ color: "rgba(126, 154, 181, 0.3)" }}
                                className={clsx(
                                  styles.userInputFieldsProps,
                                  styles.visibilityIcon
                                )}
                              />
                            ),
                            className: clsx(styles.loginFields),
                            inputProps: {
                              className: clsx(styles.loginFieldsinputProps),
                            },
                          }}
                        />
                      </div>
                      <div
                        className={`${styles.inputGrid} ${styles.userFormFields}`}
                      >
                        <TextField
                          error={
                            formik.touched.password && formik.errors.password
                          }
                          required={false}
                          fullWidth
                          name="password"
                          label="Password"
                          type={showPassword ? "text" : "password"}
                          id="password"
                          autoComplete="new-password"
                          variant="outlined"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.password}
                          InputLabelProps={{
                            className: styles.userInputFields,
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                  className={styles.visibilityIconButton}
                                >
                                  {showPassword ? (
                                    <VisibilityOff
                                      style={{
                                        color: "rgba(126, 154, 181, 0.3)",
                                      }}
                                      className={styles.visibilityIcon}
                                    />
                                  ) : (
                                    <Visibility
                                      style={{
                                        color: "rgba(126, 154, 181, 0.3)",
                                      }}
                                      className={styles.visibilityIcon}
                                    />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                            className: clsx(styles.loginFields),
                            inputProps: {
                              className: clsx(styles.loginFieldsinputProps),
                            },
                          }}
                        />
                      </div>
                    </div>

                    <div className={styles.inputGrid}>
                      <div className={styles.loginButtonContainer}>
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          className={`${styles.loginButton}`}
                        >
                          <ButtonTextLoading
                            isSubmitting={isSubmitting}
                            body={"Login"}
                          />
                        </Button>
                      </div>
                    </div>
                    <div className={styles.supportButtonDiv}>
                      <Link to="/contact" style={{ textDecoration: "none" }}>
                        <Button
                          variant="outlined"
                          className={clsx(
                            styles.darkButton,
                            styles.supportButton
                          )}
                        >
                          <ChatBubbleIcon className={styles.supportIcon} />{" "}
                          Support
                        </Button>
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.flexColumnCenter}>
            <div className={styles.logoContainerTab}>
              <LogoPNG
                style={{
                  width: "200px",
                  height: "60px",
                  verticalAlign: "middle",
                }}
              />
            </div>

            <div className={styles.containerOutlineTab}>
              <div className={styles.container}>
                <form
                  onSubmit={formik.handleSubmit}
                  className="form"
                  noValidate
                >
                  <div className={styles.loginFieldsDiv}>
                    <div
                      className={`${styles.inputGrid} ${styles.userFormFields}`}
                    >
                      <TextField
                        error={
                          formik.touched.username && formik.errors.username
                        }
                        required={false}
                        fullWidth
                        id="username"
                        label="User ID"
                        name="username"
                        autoComplete="username"
                        type="username"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.username}
                        InputLabelProps={{
                          className: styles.userInputFields,
                        }}
                        InputProps={{
                          endAdornment: (
                            <PersonIcon
                              style={{ color: "rgba(126, 154, 181, 0.3)" }}
                              className={styles.userInputFieldsProps}
                            />
                          ),
                        }}
                      />
                    </div>
                    <div
                      className={`${styles.inputGrid} ${styles.userFormFields}`}
                    >
                      <TextField
                        error={
                          formik.touched.password && formik.errors.password
                        }
                        required={false}
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password}
                        InputLabelProps={{
                          className: styles.userInputFields,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                                className={styles.visibilityIconButton}
                              >
                                {showPassword ? (
                                  <VisibilityOff
                                    style={{
                                      color: "rgba(126, 154, 181, 0.3)",
                                    }}
                                  />
                                ) : (
                                  <Visibility
                                    style={{
                                      color: "rgba(126, 154, 181, 0.3)",
                                    }}
                                  />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                  </div>

                  <div className={styles.inputGrid}>
                    <div className={styles.loginButtonContainer}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        className={`${styles.loginButton}`}
                      >
                        <ButtonTextLoading
                          isSubmitting={isSubmitting}
                          body={"Login"}
                        />
                      </Button>
                    </div>
                  </div>
                  <div className={styles.supportButtonDiv}>
                    <Link to="/contact" style={{ textDecoration: "none" }}>
                      <Button
                        variant="outlined"
                        className={clsx(
                          styles.darkButton,
                          styles.supportButton
                        )}
                      >
                        <ChatBubbleIcon className={styles.supportIcon} />{" "}
                        Support
                      </Button>
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </Container>
    </>
  );
}

export default Login;
